import { Timestamp } from 'firebase/firestore';
import { Dog, BirthRecord } from '@/types/dog';

/**
 * 出産可能ステータス
 */
export type BreedingStatus = 'available' | 'resting' | 'never_bred';

/**
 * 出産可能判定結果
 */
export interface BreedingStatusResult {
  status: BreedingStatus;
  nextAvailableDate: Date | null; // 次回交配可能日
  lastBirthDate: Date | null; // 最終出産日
  daysUntilAvailable: number | null; // 交配可能まであと何日
}

/**
 * 休息期間（日数）
 * 最終出産からこの期間経過後に交配可能と判定
 */
const REST_PERIOD_DAYS = 180; // 6ヶ月 = 約180日

/**
 * メス犬の出産可能ステータスを判定
 *
 * @param dog - 判定対象の犬
 * @param birthRecords - その犬の出産記録一覧
 * @returns 出産可能判定結果
 */
export function calculateBreedingStatus(
  dog: Dog,
  birthRecords: BirthRecord[]
): BreedingStatusResult {
  // メス犬以外は判定対象外
  if (dog.gender !== 'female') {
    return {
      status: 'never_bred',
      nextAvailableDate: null,
      lastBirthDate: null,
      daysUntilAvailable: null,
    };
  }

  // 出産記録がない場合
  if (birthRecords.length === 0) {
    return {
      status: 'never_bred',
      nextAvailableDate: null,
      lastBirthDate: null,
      daysUntilAvailable: null,
    };
  }

  // 最新の出産記録を取得
  const sortedRecords = [...birthRecords].sort((a, b) => {
    return b.birthDate.toMillis() - a.birthDate.toMillis();
  });

  const latestRecord = sortedRecords[0];
  const lastBirthDate = latestRecord.birthDate.toDate();

  // 次回交配可能日を計算
  const nextAvailableDate = new Date(lastBirthDate);
  nextAvailableDate.setDate(nextAvailableDate.getDate() + REST_PERIOD_DAYS);

  // 現在日時
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // あと何日で交配可能か計算
  const daysUntilAvailable = Math.ceil(
    (nextAvailableDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  // ステータス判定
  const status: BreedingStatus = daysUntilAvailable <= 0 ? 'available' : 'resting';

  return {
    status,
    nextAvailableDate,
    lastBirthDate,
    daysUntilAvailable: daysUntilAvailable > 0 ? daysUntilAvailable : 0,
  };
}

/**
 * 出産可能ステータスのラベルを取得
 *
 * @param status - ステータス
 * @returns 日本語ラベル
 */
export function getBreedingStatusLabel(status: BreedingStatus): string {
  switch (status) {
    case 'available':
      return '交配可能';
    case 'resting':
      return '休息推奨';
    case 'never_bred':
      return '未出産';
    default:
      return '不明';
  }
}

/**
 * 出産可能ステータスの色を取得（Chakra UI用）
 *
 * @param status - ステータス
 * @returns カラーパレット名
 */
export function getBreedingStatusColor(status: BreedingStatus): string {
  switch (status) {
    case 'available':
      return 'green';
    case 'resting':
      return 'yellow';
    case 'never_bred':
      return 'gray';
    default:
      return 'gray';
  }
}

/**
 * 指定期間内に交配記録があるかチェック
 *
 * @param dogId - 犬のID
 * @param matingRecords - 全交配記録
 * @param periodFrom - 期間開始日
 * @param periodTo - 期間終了日
 * @returns 該当する交配記録があるか
 */
export function hasMatingRecordInPeriod(
  dogId: string,
  matingRecords: { maleId: string; femaleId: string; matingDate: Timestamp }[],
  periodFrom: Date,
  periodTo: Date
): boolean {
  const fromTime = periodFrom.getTime();
  const toTime = periodTo.getTime();

  return matingRecords.some((record) => {
    // この犬が関わっている記録か
    if (record.maleId !== dogId && record.femaleId !== dogId) {
      return false;
    }

    const matingTime = record.matingDate.toDate().getTime();
    return matingTime >= fromTime && matingTime <= toTime;
  });
}

/**
 * 指定期間内に出産記録があるかチェック
 *
 * @param dogId - 犬のID
 * @param birthRecords - 全出産記録
 * @param periodFrom - 期間開始日
 * @param periodTo - 期間終了日
 * @returns 該当する出産記録があるか
 */
export function hasBirthRecordInPeriod(
  dogId: string,
  birthRecords: BirthRecord[],
  periodFrom: Date,
  periodTo: Date
): boolean {
  const fromTime = periodFrom.getTime();
  const toTime = periodTo.getTime();

  return birthRecords.some((record) => {
    // この犬が関わっている記録か（母犬または父犬）
    if (record.motherId !== dogId && record.fatherId !== dogId) {
      return false;
    }

    const birthTime = record.birthDate.toDate().getTime();
    return birthTime >= fromTime && birthTime <= toTime;
  });
}
